import React, { useState, useCallback, useRef, useEffect } from 'react';
import { TablaLogisticaVista } from '../../vista/tablaLogisticaVista';
import type { LogisticaCatalogos } from '../../vista/constantesTablaLogistica';
import type { IConfiguracionLogistica, IFiltroLogistica } from '../../interfaces';
import { LogisticaInicial } from '../../interfaces';
import { useCargaMasivaMatriz } from '../useCargaMasivaMatriz';
import { mensajeBorradoFilaMatriz } from '../usoMatriz';
import { useNotificaciones } from '../../../../globales/contextos/notificationContext';
import { useAvisoErrorConsulta } from '../useAvisoErrorConsulta';
import { EnumTipoNotificacion } from '../../../../globales/interfaces';
import { CotizadorModelo } from '../../../../modelos/CotizadorNBS';
import { useConfirmacion } from '../../../../globales/contextos/confirmacionContext';
import { useCargando } from '../../../../globales/contextos/cargandoContext';

interface LogisticaCVProps {
  datosIniciales?: IConfiguracionLogistica[];
  onRegistrarCancelacion?: (key: string, canceler: () => void) => void;
  onIniciarEdicionGeneral?: () => void;
}
import { usePaisConfiguracion } from '../paisConfiguracion';
import { esFilaNueva, idFilaNueva } from '../filaNuevaMatriz';

export const LogisticaCV: React.FC<LogisticaCVProps> = ({
  datosIniciales = LogisticaInicial,
  onRegistrarCancelacion,
  onIniciarEdicionGeneral
}) => {
  const [logistica, setLogistica] = useState<IConfiguracionLogistica[]>(datosIniciales);
  const [filaEnEdicion, setFilaEnEdicion] = useState<string | null>(null);
  const [respaldoFila, setRespaldoFila] = useState<any>(null);
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const [catalogos, setCatalogos] = useState<LogisticaCatalogos>({ productos: [], maquinas: [], operadores: [] });
  const { mostrarNotificacion } = useNotificaciones();
  const { confirmar } = useConfirmacion();
  const { manejarCargando } = useCargando();
  const paisId = usePaisConfiguracion();
  const avisarErrorConsulta = useAvisoErrorConsulta();

  // Paginación en el back (como Materiales/Tareas): se consulta solo la página visible.
  const TAMANIO_PAGINA = 15;
  const [indicePagina, setIndicePagina] = useState(0);
  const [totalFilas, setTotalFilas] = useState(0);
  // Filtros: lo que se está eligiendo y lo que ya se consultó (la paginación usa lo consultado).
  const [habilitarFiltros, setHabilitarFiltros] = useState(false);
  const [filtro, setFiltro] = useState<IFiltroLogistica>({});
  const filtroAplicado = useRef<IFiltroLogistica>({});

  const mapearFila = (item: any): IConfiguracionLogistica => {
    const cantidades = Array(12).fill("");
    const slots = item.slots || item.Slots || [];
    slots.forEach((slot: any) => {
      const cant = slot.cantidad || slot.Cantidad;
      const puntaje = slot.puntaje || slot.Puntaje;
      const idx = (cant / 40) - 1;
      if (idx >= 0 && idx < 12) {
        cantidades[idx] = puntaje;
      }
    });
    return {
      Id: String(item.id || item.Id),
      Producto: String(item.productoId ?? item.ProductoId ?? ''),
      Maquina: String(item.maquinaId ?? item.MaquinaId ?? ''),
      Operador: String(item.operadorPuertaId ?? item.OperadorPuertaId ?? ''),
      Paradas: item.paradas || item.Paradas,
      Cantidades: cantidades
    };
  };

  const cargarPagina = useCallback(async (pagina: number) => {
    try {
      const modelo = new CotizadorModelo();
      const res = await modelo.obtenerMatricesPaginado<any>('logistica', paisId as number, { Page: pagina + 1, Size: TAMANIO_PAGINA },
        { productoId: filtroAplicado.current.ProductoId, maquinaId: filtroAplicado.current.MaquinaId });
      setLogistica((res?.Data ?? []).map(mapearFila));
      setTotalFilas(res?.Pagination?.Total ?? res?.Data?.length ?? 0);
      setIndicePagina(pagina);
    } catch (error) {
      avisarErrorConsulta(error, 'la matriz de logística');
    }
  }, [paisId, avisarErrorConsulta]);

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const opciones = await new CotizadorModelo().obtenerOpcionesEquipos();
        setCatalogos({
          productos: opciones.ascensores.productos,
          maquinas: opciones.ascensores.maquinas,
          operadores: opciones.ascensores.operadoresPuerta
        });
      } catch (error) {
        avisarErrorConsulta(error, 'los catálogos de producto, máquina y operador');
      }
    };
    cargarCatalogos();
    cargarPagina(0);
  }, [cargarPagina]);

  const mostrarNotificacionObligatorio = useCallback(() => {
    setIntentoGuardar(true);
    mostrarNotificacion(
      EnumTipoNotificacion.Warning,
      'Campos Obligatorios',
      'Debes diligenciar todos los campos obligatorios antes de guardar la fila.'
    );
  }, [mostrarNotificacion]);

  const respaldoFilaRef = useRef(respaldoFila);
  useEffect(() => {
    respaldoFilaRef.current = respaldoFila;
  }, [respaldoFila]);

  const cancelarEdicion = useCallback(() => {
    setIntentoGuardar(false);
    setFilaEnEdicion(null);
    const respaldo = respaldoFilaRef.current;
    if (respaldo) {
      if (respaldo.__esNueva || esFilaNueva(respaldo.Id)) {
        setLogistica(prev => prev.filter(f => f.Id !== respaldo.Id));
      } else {
        const { __esNueva, ...limpio } = respaldo;
        setLogistica(prev => prev.map(f => f.Id === limpio.Id ? limpio : f));
      }
      setRespaldoFila(null);
    }
  }, []);

  useEffect(() => {
    onRegistrarCancelacion?.('logistica', cancelarEdicion);
  }, [onRegistrarCancelacion, cancelarEdicion]);

  const actualizarFila = useCallback((
    id: string, 
    campo: keyof IConfiguracionLogistica | 'Cantidad', 
    valor: string | number, 
    indiceCantidad?: number
  ) => {
    setLogistica(prev => prev.map(fila => {
      if (fila.Id === id) {
        if (campo === 'Cantidades' && indiceCantidad !== undefined) {
          const nuevasCantidades = Array.isArray(fila.Cantidades) ? [...fila.Cantidades] : Array(12).fill("");
          // Vacío se conserva como "" para que la validación lo detecte (Number("") daría 0).
          nuevasCantidades[indiceCantidad] = (valor === "" ? "" : Number(valor)) as number;
          return { ...fila, Cantidades: nuevasCantidades };
        }
        return { ...fila, [campo]: valor };
      }
      return fila;
    }));
  }, []);

  const eliminarFila = useCallback(async (id: string) => {
    const isNew = esFilaNueva(id);
    // Cotizaciones abiertas que usan esta combinación: se avisa antes de borrar (las Aceptadas no se recalculan).
    let mensaje = '¿Está seguro de que desea eliminar esta fila? Esta acción no se puede deshacer.';
    if (!isNew) {
      // Cargando mientras se consulta qué cotizaciones usan la fila.
      manejarCargando(true, undefined, true);
      try {
        const uso = (await new CotizadorModelo().obtenerUsoFilaMatriz('logistica', Number(id))).Data ?? [];
        mensaje = mensajeBorradoFilaMatriz(uso,
          'Si la eliminas, al recalcularlas quedarán sin contenedores ni flete hasta que se vuelva a cargar la combinación.', mensaje);
      } catch (error) {
        console.error('No se pudo consultar el uso de la fila', error);
      } finally {
        manejarCargando(false);
      }
    }

    const confirmado = await confirmar({
      titulo: 'Eliminar fila',
      mensaje,
      textoAceptar: 'Sí, eliminar',
      tipo: EnumTipoNotificacion.Warning,
      opcionCancelar: true
    });

    if (confirmado) {
      try {
        if (!isNew) {
          const modelo = new CotizadorModelo();
          await modelo.eliminarMatrizFila('logistica', Number(id));
          // Recargar la página para completarla con la siguiente fila; si quedó vacía, ir a la anterior.
          const ultimaPagina = Math.max(0, Math.ceil((totalFilas - 1) / TAMANIO_PAGINA) - 1);
          await cargarPagina(Math.min(indicePagina, ultimaPagina));
        } else {
          setLogistica(prev => prev.filter(fila => fila.Id !== id));
        }
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Eliminada', 'La fila se eliminó correctamente.');
      } catch (error) {
        console.error(error);
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', 'Ocurrió un error al eliminar la fila.');
      }
    }
  }, [mostrarNotificacion, confirmar, manejarCargando, totalFilas, indicePagina, cargarPagina]);

  const agregarFila = useCallback(() => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    const nuevaFila: IConfiguracionLogistica = {
      Id: idFilaNueva(),
      Producto: "",
      Maquina: "",
      Operador: "",
      Paradas: "",
      Cantidades: Array(12).fill("" as any)
    };
    setLogistica(prev => [nuevaFila, ...prev]);
    setRespaldoFila({ ...nuevaFila, __esNueva: true });
    setFilaEnEdicion(nuevaFila.Id);
  }, [onIniciarEdicionGeneral]);

  const guardarFila = useCallback(async (id: string) => {
    const filaToSave = logistica.find(f => f.Id === id);
    if (!filaToSave) return;
    
    try {
      const modelo = new CotizadorModelo();
      const isNew = esFilaNueva(id);
      const dbId = isNew ? null : Number(id);

      // Las 12 cantidades ya vienen validadas (obligatorias, 0 es válido).
      const cantidadesBase = [40, 80, 120, 160, 200, 240, 280, 320, 360, 400, 440, 480];
      const slots = filaToSave.Cantidades.map((puntaje, idx) => ({
        Cantidad: cantidadesBase[idx],
        Puntaje: Number(puntaje)
      }));

      const payload = {
        PaisId: paisId,
        ProductoId: Number(filaToSave.Producto),
        // Sin máquina = la fila aplica a cualquier máquina.
        MaquinaId: filaToSave.Maquina ? Number(filaToSave.Maquina) : null,
        // Sin operador = la fila aplica a cualquier puerta.
        OperadorPuertaId: filaToSave.Operador ? Number(filaToSave.Operador) : null,
        Paradas: Number(filaToSave.Paradas),
        Slots: slots
      };

      const res = await modelo.guardarMatrizFila('logistica', dbId, payload);

      if (res?.NotificationType === 1) { // Success
        if (isNew && res.Data) {
          setLogistica(prev => prev.map(f => f.Id === id ? { ...f, Id: String(res.Data) } : f));
          setTotalFilas(t => t + 1);
        }
        setIntentoGuardar(false);
        setFilaEnEdicion(null);
        setRespaldoFila(null);
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Guardada', 'La logística se guardó correctamente.');
      } else {
        mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', res?.Messages?.[0] || 'No se pudo guardar la fila.');
      }
    } catch (error: any) {
      console.error(error);
      mostrarNotificacion(EnumTipoNotificacion.Error, 'Error', error?.mensajeBackend || 'Ocurrió un error inesperado al guardar la fila.');
    }
  }, [logistica, mostrarNotificacion]);

  const iniciarEdicion = useCallback((fila: any) => {
    setIntentoGuardar(false);
    onIniciarEdicionGeneral?.();
    setRespaldoFila({ ...fila });
    setFilaEnEdicion(fila.Id);
  }, [onIniciarEdicionGeneral]);

  // Al cambiar de página se descarta la edición en curso (la fila no estará en la página nueva).
  const cambiarPagina = useCallback((pagina: number) => {
    if (filaEnEdicion) cancelarEdicion();
    cargarPagina(pagina);
  }, [filaEnEdicion, cancelarEdicion, cargarPagina]);

  const cambiarFiltro = useCallback((campo: keyof IFiltroLogistica, valor: number | undefined) => {
    setFiltro(prev => ({ ...prev, [campo]: valor }));
  }, []);

  const consultarFiltro = useCallback((nuevo: IFiltroLogistica) => {
    if (filaEnEdicion) cancelarEdicion();
    filtroAplicado.current = nuevo;
    cargarPagina(0);
  }, [filaEnEdicion, cancelarEdicion, cargarPagina]);

  const limpiarFiltro = useCallback(() => {
    setFiltro({});
    consultarFiltro({});
  }, [consultarFiltro]);

  const { descargarPlantilla, cargaMasiva, modalResultado } = useCargaMasivaMatriz({
    matriz: 'logistica',
    etiquetaReferencia: 'Combinación',
    antesDeCargar: () => { if (filaEnEdicion) cancelarEdicion(); },
    alTerminar: () => cargarPagina(0)
  });

  return (
    <>
    {modalResultado}
    <TablaLogisticaVista
      alRecargar={() => { cancelarEdicion(); cargarPagina(indicePagina); }}
      datos={logistica}
      alDescargarPlantilla={descargarPlantilla}
      alCargaMasiva={cargaMasiva}
      paginacion={{ indicePagina, tamanioPagina: TAMANIO_PAGINA, total: totalFilas, alCambiarPagina: cambiarPagina }}
      actualizarFila={actualizarFila}
      eliminarFila={eliminarFila}
      guardarFila={guardarFila}
      filaEnEdicion={filaEnEdicion}
      iniciarEdicion={iniciarEdicion}
      cancelarEdicion={cancelarEdicion}
      agregarFila={agregarFila}
      mostrarNotificacionObligatorio={mostrarNotificacionObligatorio}
      intentoGuardar={intentoGuardar}
      catalogos={catalogos}
      filtros={{
        habilitados: habilitarFiltros,
        alAlternar: () => setHabilitarFiltros(h => !h),
        filtro,
        alCambiarFiltro: cambiarFiltro,
        alConsultar: () => consultarFiltro(filtro),
        alLimpiar: limpiarFiltro
      }}
    />
    </>
  );
};
